import {
	Button,
	Cell,
	Checkbox,
	Column,
	Row,
	SearchField,
	TableBody,
	TableHeader,
	TableView,
	Text,
} from '@react-spectrum/s2';
import PeopleGroup from '@react-spectrum/s2/icons/PeopleGroup';
import {
	iconStyle,
	style,
} from '@react-spectrum/s2/style' with { type: 'macro' };
import { memo, useMemo, useState } from 'react';
import type {
	Key,
	KeyboardEvent,
	PointerEvent as ReactPointerEvent,
} from 'react';
import type { Selection, SortDescriptor } from 'react-aria-components';

import { AnimateIn } from '@/components/AnimateIn';
import type { Audience } from '@/spaces/audiences/types';
import { EmptyState } from '@/spaces/people/EmptyState';

import type { Contact } from './types';
import { formatRelativeTime, getDisplayName, getLastSeen } from './utils';

const getSortableTime = (contact: Contact): number => {
	const lastSeen = getLastSeen(contact);
	if (!lastSeen) return 0;
	const time = new Date(lastSeen).getTime();
	return Number.isNaN(time) ? 0 : time;
};

interface ContactsListProps {
	contacts: Contact[];
	onContactClick: (contact: Contact) => void;
	onCreate?: () => void;
	onLoadMore?: () => void;
	isLoading?: boolean;
	isLoadingMore?: boolean;
	isSearching?: boolean;
	hasMore?: boolean;
	error?: string | null;
	searchQuery?: string;
	onSearchChange?: (query: string) => void;
	audiences?: Audience[];
	selectedContactIds?: Set<string>;
	onSelectionChange?: (ids: Set<string>) => void;
	onClearSelection?: () => void;
	onBulkAddToAudience?: (audienceId: string) => Promise<void> | void;
	onBulkRemoveFromAudience?: (audienceId: string) => Promise<void> | void;
	onCreateAudienceForSelection?: () => void;
	bulkError?: string | null;
	isBulkSaving?: boolean;
}

export const ContactsList = memo(function ContactsList({
	contacts,
	onContactClick,
	onCreate,
	onLoadMore,
	isLoading = false,
	isLoadingMore = false,
	isSearching = false,
	hasMore = false,
	error = null,
	searchQuery = '',
	onSearchChange,
	audiences = [],
	selectedContactIds,
	onSelectionChange,
	onClearSelection,
	onBulkAddToAudience,
	onBulkRemoveFromAudience,
	onCreateAudienceForSelection,
	bulkError = null,
	isBulkSaving = false,
}: ContactsListProps) {
	const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
		column: 'lastSeen',
		direction: 'descending',
	});
	const [paletteMode, setPaletteMode] = useState<null | 'root' | 'manage'>(
		null,
	);
	const [paletteQuery, setPaletteQuery] = useState('');
	const [paletteChecked, setPaletteChecked] = useState<Set<string>>(
		() => new Set(),
	);

	const audienceNameById = useMemo(() => {
		const map = new Map<string, string>();
		for (const audience of audiences) {
			map.set(audience.id, audience.name);
		}
		return map;
	}, [audiences]);

	const sortedContacts = useMemo(() => {
		const audienceKeyFor = (contact: Contact): string => {
			const names: string[] = [];
			for (const id of contact.audience_ids ?? []) {
				const name = audienceNameById.get(id);
				if (name) names.push(name);
			}
			if (names.length === 0) return '';
			names.sort((a, b) =>
				a.localeCompare(b, undefined, { sensitivity: 'base' }),
			);
			return names[0];
		};

		const list = [...contacts];
		const { column, direction } = sortDescriptor;
		list.sort((a, b) => {
			let cmp = 0;
			if (column === 'name') {
				cmp = getDisplayName(a).localeCompare(getDisplayName(b), undefined, {
					sensitivity: 'base',
				});
			} else if (column === 'audiences') {
				const ka = audienceKeyFor(a);
				const kb = audienceKeyFor(b);
				if (ka === '' && kb !== '') cmp = 1;
				else if (kb === '' && ka !== '') cmp = -1;
				else cmp = ka.localeCompare(kb, undefined, { sensitivity: 'base' });
			} else {
				cmp = getSortableTime(a) - getSortableTime(b);
			}
			return direction === 'descending' ? -cmp : cmp;
		});
		return list;
	}, [contacts, sortDescriptor, audienceNameById]);

	if (isLoading) {
		return (
			<div
				className="flex h-full flex-col items-center justify-center gap-4 py-16"
			>
				<Text
					styles={style({
						font: 'body',
						fontSize: 'body-lg',
						color: 'body',
					})}
				>
					Loading contacts...
				</Text>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex h-full flex-col items-center justify-center py-16">
				<AnimateIn delay={0}>
					<h2
						className="mb-2 text-center text-2xl font-bold tracking-tight
							text-(--text-primary)"
					>
						Unable to load contacts
					</h2>
				</AnimateIn>
				<AnimateIn delay={1}>
					<p
						className="mb-8 max-w-md text-center text-sm leading-relaxed
							text-(--text-muted)"
					>
						{error}
					</p>
				</AnimateIn>
			</div>
		);
	}

	if (contacts.length === 0 && !searchQuery) {
		return (
			<EmptyState
				icon={<PeopleGroup styles={iconStyle({ size: 'XL' })} />}
				heading="No contacts yet"
				subtext="Add your first contact to start organizing them into audiences."
				ctaLabel={onCreate ? 'New contact' : undefined}
				onAction={onCreate}
			/>
		);
	}

	const handleAction = (key: Key) => {
		const contact = sortedContacts.find((c) => c.id === key);
		if (contact) onContactClick(contact);
	};

	const handleSpectrumSelectionChange = (keys: Selection) => {
		if (!onSelectionChange) return;
		if (keys === 'all') {
			onSelectionChange(new Set(sortedContacts.map((c) => c.id)));
		} else {
			onSelectionChange(new Set(Array.from(keys).map((k) => String(k))));
		}
	};

	const isClickOnSelectionCheckbox = (target: HTMLElement): boolean => {
		if (target.closest('[slot="selection"]')) return true;
		if (target.closest('input[type="checkbox"]')) return true;
		if (target.closest('[role="checkbox"]')) return true;
		const cell = target.closest(
			'[role="rowheader"], [role="gridcell"], [role="columnheader"]',
		);
		if (
			cell &&
			cell.querySelector(
				'[slot="selection"], input[type="checkbox"], [role="checkbox"]',
			)
		)
			return true;
		return false;
	};

	const handleTableWrapperPointerDownCapture = (
		e: ReactPointerEvent<HTMLDivElement>,
	) => {
		if (!onSelectionChange) return;
		const target = e.target as HTMLElement;
		if (isClickOnSelectionCheckbox(target)) return;
		const row = target.closest('[role="row"]') as HTMLElement | null;
		if (!row) return;
		const rowKey = row.getAttribute('data-key');
		if (!rowKey) return;
		e.stopPropagation();
		handleAction(rowKey);
	};

	const selectionSize = selectedContactIds?.size ?? 0;
	const showBulkToolbar = selectionSize > 0;
	const audiencesAvailable = audiences.length > 0;

	const openPalette = () => {
		setPaletteQuery('');
		setPaletteChecked(new Set());
		setPaletteMode('root');
	};
	const closePalette = () => {
		setPaletteMode(null);
		setPaletteQuery('');
		setPaletteChecked(new Set());
	};
	const computeAssignedUnion = (): Set<string> => {
		const assigned = new Set<string>();
		const selected = selectedContactIds ?? new Set<string>();
		for (const contact of contacts) {
			if (!selected.has(contact.id)) continue;
			for (const id of contact.audience_ids ?? []) {
				assigned.add(id);
			}
		}
		return assigned;
	};

	const enterManageMode = () => {
		setPaletteQuery('');
		setPaletteChecked(computeAssignedUnion());
		setPaletteMode('manage');
	};

	const hasPaletteChanges = (): boolean => {
		if (paletteMode !== 'manage') return false;
		const initial = computeAssignedUnion();
		if (initial.size !== paletteChecked.size) return true;
		for (const id of paletteChecked) {
			if (!initial.has(id)) return true;
		}
		return false;
	};

	const matchesQuery = (label: string) => {
		const q = paletteQuery.trim().toLowerCase();
		if (!q) return true;
		return label.toLowerCase().includes(q);
	};

	type RootItem = {
		id: string;
		label: string;
		isDisabled?: boolean;
		onSelect: () => void;
	};

	const rootItems: RootItem[] = [
		{
			id: 'manage',
			label: 'Manage audience…',
			isDisabled:
				!audiencesAvailable ||
				(!onBulkAddToAudience && !onBulkRemoveFromAudience),
			onSelect: enterManageMode,
		},
		{
			id: 'create',
			label: 'Create audience…',
			isDisabled: !onCreateAudienceForSelection,
			onSelect: () => {
				closePalette();
				onCreateAudienceForSelection?.();
			},
		},
	];

	const visibleRootItems = rootItems.filter((i) => matchesQuery(i.label));
	const visibleAudiences = audiences.filter((a) => matchesQuery(a.name));

	const toggleChecked = (id: string) => {
		setPaletteChecked((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const applyChecklist = async () => {
		if (paletteMode !== 'manage') return;
		const initial = computeAssignedUnion();
		const toAdd: string[] = [];
		const toRemove: string[] = [];
		for (const audience of audiences) {
			const isChecked = paletteChecked.has(audience.id);
			const wasAssigned = initial.has(audience.id);
			if (isChecked && !wasAssigned) toAdd.push(audience.id);
			else if (!isChecked && wasAssigned) toRemove.push(audience.id);
		}
		closePalette();
		if (onBulkAddToAudience) {
			for (const id of toAdd) await onBulkAddToAudience(id);
		}
		if (onBulkRemoveFromAudience) {
			for (const id of toRemove) await onBulkRemoveFromAudience(id);
		}
	};

	const handlePaletteKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			if (paletteMode === 'root') {
				const first = visibleRootItems.find((i) => !i.isDisabled);
				if (first) first.onSelect();
				return;
			}
			void applyChecklist();
			return;
		}
		if (
			e.key === 'Backspace' &&
			paletteQuery === '' &&
			paletteMode !== 'root'
		) {
			e.preventDefault();
			setPaletteMode('root');
			setPaletteChecked(new Set());
		}
	};

	return (
		<div className="relative flex h-full min-h-0 flex-col gap-6">
			<AnimateIn delay={0}>
				<div className="flex w-full flex-col gap-2">
					{bulkError && (
						<div
							data-testid="contacts-bulk-error"
							className="rounded-md border border-(--glass-border) px-3 py-2"
						>
							<Text
								styles={style({
									font: 'body',
									fontSize: 'body-sm',
									color: 'negative',
								})}
							>
								{bulkError}
							</Text>
						</div>
					)}
					<SearchField
						aria-label="Search contacts"
						placeholder="Search by name, email, phone, or Instagram..."
						value={searchQuery}
						onChange={(value) => onSearchChange?.(value)}
					/>
				</div>
			</AnimateIn>

			<AnimateIn delay={1} className="flex min-h-0 flex-1 flex-col">
				{isSearching ? (
					<Text
						styles={style({
							font: 'body',
							fontSize: 'body',
							color: 'body',
						})}
						UNSAFE_className="block py-8 text-center"
					>
						Searching...
					</Text>
				) : sortedContacts.length === 0 && searchQuery ? (
					<Text
						styles={style({
							font: 'body',
							fontSize: 'body',
							color: 'body',
						})}
						UNSAFE_className="block py-8 text-center"
					>
						No contacts match &quot;{searchQuery}&quot;
					</Text>
				) : (
					<div
						className="flex min-h-0 flex-1 flex-col
							[&_[role=progressbar]]:scale-50 [&_[role=row]]:cursor-pointer"
						onPointerDownCapture={handleTableWrapperPointerDownCapture}
					>
						<TableView
							aria-label="Contacts"
							styles={style({ width: 'full', height: 'full' })}
							onAction={handleAction}
							overflowMode="wrap"
							isQuiet
							density="spacious"
							sortDescriptor={sortDescriptor}
							onSortChange={setSortDescriptor}
							selectionMode={onSelectionChange ? 'multiple' : 'none'}
							selectedKeys={selectedContactIds ?? new Set()}
							onSelectionChange={
								onSelectionChange ? handleSpectrumSelectionChange : undefined
							}
							loadingState={isLoadingMore ? 'loadingMore' : 'idle'}
							onLoadMore={
								hasMore && !searchQuery && !isLoadingMore
									? onLoadMore
									: undefined
							}
						>
							<TableHeader>
								<Column id="name" isRowHeader defaultWidth="2fr" allowsSorting>
									ID
								</Column>
								<Column id="audiences" defaultWidth="2fr" allowsSorting>
									Audiences
								</Column>
								<Column id="lastSeen" allowsSorting>
									Last Seen
								</Column>
							</TableHeader>
							<TableBody items={sortedContacts}>
								{(contact) => (
									<Row id={contact.id}>
										<Cell>{getDisplayName(contact)}</Cell>
										<Cell>
											<div
												className="flex flex-wrap gap-1 py-2"
												data-testid={`contact-audiences-${contact.id}`}
											>
												{(contact.audience_ids ?? []).map((audienceId) => {
													const name = audienceNameById.get(audienceId);
													if (!name) return null;
													return (
														<span
															key={audienceId}
															className="inline-flex items-center gap-1
																rounded-full border border-(--glass-border)
																bg-(--surface-subtle) px-2 py-0.5 text-xs
																text-(--text-primary)"
															data-testid={`audience-pill-${contact.id}-${audienceId}`}
														>
															{name}
														</span>
													);
												})}
											</div>
										</Cell>
										<Cell>{formatRelativeTime(getLastSeen(contact))}</Cell>
									</Row>
								)}
							</TableBody>
						</TableView>
					</div>
				)}
			</AnimateIn>

			{showBulkToolbar && (
				<div
					data-testid="contacts-bulk-toolbar"
					className="pointer-events-none absolute right-0 bottom-2 left-0 z-10
						flex justify-center"
				>
					<div
						className="pointer-events-auto flex items-center gap-2 rounded-full
							border border-(--glass-border) bg-white px-3 py-2 shadow-lg"
					>
						<Text
							styles={style({
								font: 'body',
								fontSize: 'body-sm',
								color: 'body',
							})}
						>
							{selectionSize} selected
						</Text>
						<button
							type="button"
							aria-label="Clear selection"
							data-testid="bulk-clear"
							className="flex h-6 w-6 items-center justify-center rounded-full
								text-(--text-muted) hover:bg-(--surface-subtle)
								hover:text-(--text-primary)"
							onClick={() => onClearSelection?.()}
						>
							×
						</button>
						<span className="mx-1 h-4 w-px bg-(--glass-border)" />
						<Button
							variant="secondary"
							size="S"
							isDisabled={isBulkSaving}
							data-testid="bulk-actions-trigger"
							onPress={openPalette}
						>
							{isBulkSaving ? 'Saving…' : 'Actions'}
						</Button>
					</div>
				</div>
			)}

			{paletteMode !== null && (
				<div
					data-testid="bulk-actions-palette-overlay"
					className="fixed inset-0 z-50 flex items-center justify-center
						bg-black/40"
					onClick={closePalette}
				>
					<div
						data-testid="bulk-actions-palette"
						className="flex max-h-[70vh] w-full max-w-xl flex-col gap-2
							rounded-lg border border-(--glass-border) bg-white p-3 shadow-2xl"
						onClick={(e) => e.stopPropagation()}
					>
						<input
							type="text"
							autoFocus
							value={paletteQuery}
							onChange={(e) => setPaletteQuery(e.target.value)}
							onKeyDown={handlePaletteKeyDown}
							placeholder={
								paletteMode === 'root'
									? 'Type a command or search…'
									: 'Search audiences…'
							}
							data-testid="bulk-actions-palette-input"
							className="w-full bg-transparent px-2 py-2 text-base
								text-(--text-primary) placeholder:text-(--text-muted)
								focus:outline-none"
						/>
						<div className="min-h-0 flex-1 overflow-y-auto">
							{paletteMode === 'root' ? (
								visibleRootItems.length === 0 ? (
									<div
										className="px-2 py-3 text-sm text-(--text-muted)"
										data-testid="bulk-actions-palette-empty"
									>
										No results
									</div>
								) : (
									<ul className="flex flex-col">
										{visibleRootItems.map((item) => (
											<li key={item.id}>
												<button
													type="button"
													disabled={item.isDisabled}
													data-testid={`bulk-actions-palette-item-${item.id}`}
													onClick={item.onSelect}
													className="flex w-full items-center rounded-md px-2
														py-2 text-left text-sm text-(--text-primary)
														hover:bg-(--surface-subtle)
														disabled:cursor-not-allowed disabled:opacity-50
														disabled:hover:bg-transparent"
												>
													{item.label}
												</button>
											</li>
										))}
									</ul>
								)
							) : visibleAudiences.length === 0 ? (
								<div
									className="px-2 py-3 text-sm text-(--text-muted)"
									data-testid="bulk-actions-palette-empty"
								>
									No results
								</div>
							) : (
								<ul className="flex flex-col">
									{visibleAudiences.map((audience) => {
										const checked = paletteChecked.has(audience.id);
										return (
											<li key={audience.id}>
												<div
													data-testid={`bulk-actions-palette-item-${audience.id}`}
													className="flex w-full items-center gap-2 rounded-md
														px-2 py-2 text-left text-sm
														hover:bg-(--surface-subtle)"
												>
													<Checkbox
														isSelected={checked}
														onChange={() => toggleChecked(audience.id)}
														data-testid={`bulk-actions-palette-checkbox-${audience.id}`}
													>
														{audience.name}
													</Checkbox>
												</div>
											</li>
										);
									})}
								</ul>
							)}
						</div>
						{paletteMode !== 'root' && (
							<div
								className="flex items-center justify-between border-t
									border-(--glass-border) pt-2"
							>
								<span className="px-2 text-xs text-(--text-muted)">
									{paletteChecked.size} selected
								</span>
								<Button
									variant="accent"
									size="S"
									data-testid="bulk-actions-palette-apply"
									isDisabled={!hasPaletteChanges()}
									onPress={() => void applyChecklist()}
								>
									Submit
								</Button>
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
});
